import { SupabaseClient } from '@supabase/supabase-js';
import {
  AS_CONSTRUCTED_TOKENS,
  Block,
  CLIENT_PLACEHOLDER,
  CONSULTANT_PLACEHOLDER,
  CertifiedDrawing,
  DESIGN_CRITERIA_TOKEN,
  DocumentChromeVariant,
  DrawingListBlockData,
  ParagraphBlockData,
  QaCertificationRow,
  QaListBlockData,
  createInitialBlocks,
  designCriteriaParagraph,
  escapeHtml,
  generateBlockId,
  qaRowForType,
  siteVisitPhrase,
  SI_METHOD_DEFAULT,
} from './config';
import { RWC_QA_ITEMS } from '../../components/ProjectDetailModal/config';

/**
 * Shared document-creation logic.
 *
 * Both the /documents list page's create modal and the project/stage Documents
 * tab create documents the same way, so the prefill lives here rather than in
 * two drifting copies.
 *
 * The row itself is written by the create_document() RPC, not by an INSERT from
 * here. That moved server-side because a numbered template (the Site
 * Instruction's 'SI{sequence} - {title}') has to allocate its number in the same
 * transaction as the insert: a client that reads MAX + 1 and then inserts races
 * another tab, and an advisory lock inside a separate "next number please" call
 * is released the moment that call's transaction ends.
 *
 * The number is therefore expanded ONLY by expand_document_number() in SQL. A
 * client-side expander used to exist and was deleted -- do not add another.
 */

export interface CreateDocumentParams {
  templateId: string;
  projectId: number;
  /** The stage's bigint id, when creating a stage document. */
  stageId?: number | null;
  title: string;
  /** Decides which header_fields shape is prefilled. */
  chrome?: DocumentChromeVariant;
  /**
   * The template's `code`. Decides the seeded body, which the chrome cannot:
   * the design certificate and the Standard Report share the letter chrome but
   * one starts with its standardised wording and the other starts blank.
   */
  templateCode?: string | null;
  /**
   * When the template requires a certified (CPEng) signatory the creator is
   * usually the report writer, not the certifier, so the creator is NOT the
   * default. It takes the first certified engineer instead -- see
   * firstCertifiedEngineer.
   */
  requiresCertifiedSignatory?: boolean;
}

interface ClientRow {
  address_type: string | null;
  street_address: string | null;
  po_box: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
}

interface ContactRow {
  first_name: string;
  last_name: string;
}

/**
 * The letter chrome's header prefill: the client's postal address and the
 * primary contact, copied in at creation. On a stage with a primary contact of
 * its own that contact is used; otherwise (null = inherit) the project's.
 */
async function buildLetterHeaderFields(
  supabase: SupabaseClient,
  projectId: number,
  stageId: number | null,
  title: string
): Promise<Record<string, string>> {
  const [projectRes, stageRes] = await Promise.all([
    supabase
      .from('projects')
      .select(`
        street_address,
        city,
        state,
        postcode,
        country,
        clients!client ( address_type, street_address, po_box, city, state, postcode ),
        contacts!primary_contact ( first_name, last_name )
      `)
      .eq('id', projectId)
      .single(),
    stageId
      ? supabase
          .from('project_stages')
          .select('contacts:contacts!project_stages_primary_contact_fkey ( first_name, last_name )')
          .eq('id', stageId)
          .single()
      : Promise.resolve(null),
  ]);
  const { data: projectData, error } = projectRes;

  if (error || !projectData) {
    throw new Error('Error fetching project details');
  }

  const clientData = projectData.clients as unknown as ClientRow | null;
  // A failed stage read falls back to the project's contact, which is what an
  // inheriting stage would print anyway; the user can override it in the header.
  if (stageRes?.error) console.error('Error fetching stage contact:', stageRes.error);
  const stageContact = (stageRes?.data?.contacts as unknown as ContactRow | null) ?? null;
  const contactData = stageContact ?? (projectData.contacts as unknown as ContactRow | null);

  const clientStreet = clientData?.address_type === 'po_box'
    ? (clientData?.po_box ? `PO Box ${clientData.po_box}` : '')
    : (clientData?.street_address || '');

  const projectAddress = [
    projectData.street_address,
    [projectData.city, projectData.state, projectData.postcode].filter(Boolean).join(' '),
    projectData.country && projectData.country !== 'Australia' ? projectData.country : null,
  ].filter(Boolean).join(', ');

  return {
    document_date: new Date().toISOString().split('T')[0],
    contact_name: contactData ? `${contactData.first_name} ${contactData.last_name}`.trim() : '',
    dear_name: contactData?.first_name || '',
    client_street: clientStreet,
    client_city: clientData?.city || '',
    client_state: clientData?.state || '',
    client_postcode: clientData?.postcode || '',
    project_address: projectAddress,
    report_title: title || '',
  };
}

/**
 * Fill a seeded drawing_list block from the stage's drawing set.
 *
 * A COPY, not a reference. The certificate must go on saying what it said when
 * it was signed, even after the drawings are revised -- so the rows live in the
 * document's own content, where the edit lock freezes them on issue. While it
 * is a draft the block offers a Refresh that runs this same query again.
 *
 * project_drawings is stage-scoped and has no RPC; this is the direct read the
 * invoice editor already does. Site classification prefers the stage's own,
 * falling back to the project's -- the precedence care_of and head contractor
 * already use.
 *
 * Returns the rows a refresh would DROP -- ones the document holds against a
 * drawing the set no longer has. They are the only part of a refresh that
 * cannot be undone by refreshing again, so the caller confirms before applying
 * it. Rows with no sourceId were typed in by hand and are never dropped: the
 * set has no counterpart to restore them from.
 */
export async function captureDrawings(
  supabase: SupabaseClient,
  blocks: Block[],
  stageId: number | null,
  projectId: number | null
): Promise<{ blocks: Block[]; dropped: CertifiedDrawing[] }> {
  const target = blocks.find(b => b.type === 'drawing_list');
  if (!target) return { blocks, dropped: [] };

  const [drawingsRes, stageRes, projectRes] = await Promise.all([
    stageId
      ? supabase
          .from('project_drawings')
          .select('id, drawing_number, issue_number, title, is_design_standard, sort_order')
          .eq('stage_id', stageId)
          .order('sort_order')
      : Promise.resolve({ data: [] as DrawingRow[], error: null }),
    stageId
      ? supabase.from('project_stages').select('site_classification').eq('id', stageId).single()
      : Promise.resolve({ data: null }),
    // create_document refuses a document with no project, but the column is
    // nullable, so the fallback is guarded rather than asserted.
    projectId
      ? supabase.from('projects').select('site_classification').eq('id', projectId).single()
      : Promise.resolve({ data: null }),
  ]);

  // A failed read leaves `data` null, which is indistinguishable from a stage
  // with no drawings -- the certificate would be created reading "No drawings
  // listed" and nobody would know it was a failure rather than an empty set.
  // Refusing is the only honest answer; the caller shows the message.
  //
  // The two site-classification reads are NOT treated this way: losing one
  // leaves a visible "[site classification]" placeholder in the sentence, which
  // announces itself.
  if (drawingsRes.error) {
    throw new Error(`Could not read the stage's drawing set: ${drawingsRes.error.message}`);
  }

  const rows: CertifiedDrawing[] = ((drawingsRes.data as DrawingRow[] | null) || []).map(d => ({
    id: generateBlockId(),
    sourceId: d.id,
    drawing_number: d.drawing_number,
    issue_number: d.issue_number,
    title: d.title,
    is_design_standard: d.is_design_standard,
  }));

  const data = target.data as DrawingListBlockData;
  const previous = data.rows ?? [];
  const keptSourceIds = new Set(rows.map(r => r.sourceId));

  data.rows = [
    ...rows,
    // Hand-added drawings sit after the set's, which carry its sort order.
    ...previous.filter(r => r.sourceId === null),
  ];
  data.site_classification =
    (stageRes.data as { site_classification?: string | null } | null)?.site_classification ||
    (projectRes.data as { site_classification?: string | null } | null)?.site_classification ||
    null;

  return {
    blocks,
    dropped: previous.filter(r => r.sourceId !== null && !keptSourceIds.has(r.sourceId)),
  };
}

/**
 * Write the design-criteria sentence into the paragraph that holds its token,
 * composed from the drawings captureDrawings just took. Once, at creation:
 * from then on it is the engineer's paragraph (see followDesignCriteria).
 */
function captureDesignCriteria(blocks: Block[]): Block[] {
  const drawings = blocks.find(b => b.type === 'drawing_list');
  if (!drawings) return blocks;
  const sentence = designCriteriaParagraph(drawings.data as DrawingListBlockData);
  for (const block of blocks) {
    if (block.type !== 'paragraph') continue;
    const data = block.data as ParagraphBlockData;
    if (data.content?.includes(DESIGN_CRITERIA_TOKEN)) {
      data.content = data.content.split(DESIGN_CRITERIA_TOKEN).join(sentence);
    }
  }
  return blocks;
}

/**
 * Carry a drawing refresh into the design-criteria paragraph -- only while it
 * still reads exactly what the drawings BEFORE the refresh composed, i.e.
 * nobody has edited it. An edited one is the engineer's wording and is left
 * alone, the same rule the As Constructed Certificate's prose follows: a
 * refresh that rewrites a sentence somebody reworded is destructive in a way
 * replacing a row is not.
 *
 * A certificate from before the sentence was a paragraph has no such
 * paragraph -- its drawing block draws the sentence itself, from the rows --
 * so nothing here matches and nothing changes.
 */
export function followDesignCriteria(
  blocks: Block[],
  before: DrawingListBlockData,
  after: DrawingListBlockData
): Block[] {
  const was = designCriteriaParagraph(before);
  const now = designCriteriaParagraph(after);
  if (was === now) return blocks;
  return blocks.map(block =>
    block.type === 'paragraph' && (block.data as ParagraphBlockData).content === was
      ? { ...block, data: { ...block.data, content: now } as ParagraphBlockData }
      : block
  );
}

interface DrawingRow {
  id: number;
  drawing_number: string;
  issue_number: string;
  title: string;
  is_design_standard: boolean;
  sort_order: number;
}

/**
 * Reconcile an As Constructed Certificate's QA list against the RWC QA items
 * the job has actually completed.
 *
 * Membership is every QA type with a COMPLETION DATE on the job -- the results
 * actually received, which is what the certificate says it relies on. Not
 * `required`, which only records that the job was scoped to include them.
 *
 * A RECONCILE, not a re-capture. The certificate's bullets are authored -- the
 * engineer writes which laboratory reported what, and types the job number
 * underneath -- so a row this list already has keeps its text. Only membership
 * comes from the PEP:
 *
 *   - a type newly completed is added, with its seeded wording
 *   - a type whose completion date has gone is REMOVED, and returned in
 *     `dropped` so the caller can name it before that happens
 *   - a row added by hand (no qa_type) is never touched, because the PEP has no
 *     counterpart to restore it from
 *
 * Ordered by RWC_QA_ITEMS, the same order the PEP lists them in, so the
 * certificate reads down the checklist -- but membership comes from the QUERY,
 * not from that list. A qa_type the enum has and RWC_QA_ITEMS does not (a new
 * checklist item whose database change ships ahead of the frontend) still gets
 * a bullet, worded by QA_STATEMENT_FALLBACK, and sorts after the ones this
 * build knows. Filtering RWC_QA_ITEMS instead would drop such an item from the
 * certificate silently -- and drop an existing row for one without listing it
 * in `dropped`, so nothing would be confirmed either.
 */
export async function reconcileQaList(
  supabase: SupabaseClient,
  blocks: Block[],
  stageId: number | null,
  projectId: number | null
): Promise<{ blocks: Block[]; dropped: QaCertificationRow[] }> {
  const target = blocks.find(b => b.type === 'qa_list');
  if (!target) return { blocks, dropped: [] };

  // COMPLETION DATE, not `required`. `required` is scope -- which checks this
  // job was signed up for -- and a certificate that read off it would state
  // that results had been provided for work nobody has confirmed was done. The
  // date is the record that it actually happened, and it is the only thing on
  // the PEP that says so.
  const query = supabase
    .from('rwc_qa_items')
    .select('qa_type, completion_date')
    .not('completion_date', 'is', null)
    // Only so an unrecognised type sorts predictably; the checklist order below
    // decides everything this build knows about.
    .order('qa_type');
  // project XOR stage, the same shape the PEP writes them with.
  const { data, error } = stageId
    ? await query.eq('stage_id', stageId)
    : await query.eq('project_id', projectId as number);

  // Same reasoning as the drawing set: a failed read is indistinguishable from
  // "nothing is completed", and a certification that quietly omits the QA
  // results it was meant to rely on is the worst outcome this block has.
  if (error) {
    throw new Error(`Could not read the job's QA items: ${error.message}`);
  }

  const completed = new Set((data || []).map(r => (r as { qa_type: string }).qa_type));
  const existing = (target.data as QaListBlockData).rows ?? [];
  const byType = new Map(existing.filter(r => r.qa_type).map(r => [r.qa_type, r]));

  // Checklist order, with anything this build does not recognise after it.
  const checklistOrder = new Map(RWC_QA_ITEMS.map((item, i) => [item.qa_type, i]));
  const ordered = Array.from(completed).sort(
    (a, b) => (checklistOrder.get(a) ?? RWC_QA_ITEMS.length)
            - (checklistOrder.get(b) ?? RWC_QA_ITEMS.length)
  );

  const rows: QaCertificationRow[] = [
    ...ordered.map(qaType => byType.get(qaType) ?? qaRowForType(qaType)),
    // Hand-added bullets sit after the checklist, which owns the order above.
    ...existing.filter(r => !r.qa_type),
  ];

  (target.data as QaListBlockData).rows = rows;

  return {
    blocks,
    dropped: existing.filter(r => r.qa_type && !completed.has(r.qa_type)),
  };
}

/**
 * Fill an As Constructed Certificate's body from the job.
 *
 * The QA list is reconciled (above) and keeps a Refresh. The other three
 * values -- how many site visits were made, the engineering consultant, and the
 * client -- are ordinary words inside ordinary sentences, so they are written
 * in ONCE here and are editable prose afterwards. There is deliberately no
 * refresh for them: rewriting a sentence the engineer may have reworded is
 * destructive in a way replacing a row is not.
 *
 * Every substitution has a visible fallback, so a token can never reach the
 * page and a missing value asks to be filled in rather than leaving a gap.
 */
export async function captureAsConstructed(
  supabase: SupabaseClient,
  blocks: Block[],
  stageId: number | null,
  projectId: number | null
): Promise<Block[]> {
  if (!blocks.some(b => b.type === 'qa_list')) return blocks;

  const inspectionQuery = supabase
    .from('inspections')
    .select('id', { count: 'exact', head: true });

  const [{ count: visitCount }, entityRes] = await Promise.all([
    stageId
      ? inspectionQuery.eq('stage_id', stageId)
      : inspectionQuery.eq('project_id', projectId as number),
    // retaining_standard names the consultant whose drawings are certified
    // against; the client is the party the certificate says verified the rest.
    // The stage's standard wins over the project's, as care_of already does.
    stageId
      ? supabase
          .from('project_stages')
          .select('retaining_standard, projects!project_stages_parent_project_id_fkey ( retaining_standard, clients!client ( company_name ) )')
          .eq('id', stageId)
          .single()
      : supabase
          .from('projects')
          .select('retaining_standard, clients!client ( company_name )')
          .eq('id', projectId as number)
          .single(),
  ]);

  await reconcileQaList(supabase, blocks, stageId, projectId);

  const entity = entityRes.data as EntityStandardRow | null;
  const parent = (entity?.projects as unknown as EntityStandardRow | undefined) ?? undefined;
  const standard = entity?.retaining_standard || parent?.retaining_standard || null;
  const client = (parent ?? entity)?.clients?.company_name || null;

  // 'Other Engineer' is the enum's "not one of ours" value -- it names no firm,
  // so it asks for one rather than printing itself into the sentence.
  const consultant = standard && standard !== 'Other Engineer' ? standard : CONSULTANT_PLACEHOLDER;

  const replacements: Record<string, string> = {
    [AS_CONSTRUCTED_TOKENS.visits]: siteVisitPhrase(visitCount ?? null),
    [AS_CONSTRUCTED_TOKENS.inspectionNoun]: visitCount === 1 ? 'inspection' : 'inspections',
    [AS_CONSTRUCTED_TOKENS.consultant]: escapeHtml(consultant),
    [AS_CONSTRUCTED_TOKENS.client]: escapeHtml(client || CLIENT_PLACEHOLDER),
  };

  for (const block of blocks) {
    if (block.type !== 'paragraph') continue;
    const data = block.data as ParagraphBlockData;
    let content = data.content || '';
    for (const [token, value] of Object.entries(replacements)) {
      content = content.split(token).join(value);
    }
    data.content = content;
  }

  return blocks;
}

interface EntityStandardRow {
  retaining_standard: string | null;
  clients?: { company_name: string } | null;
  projects?: unknown;
}

/**
 * The blocks a new document is created with, after every template that pulls
 * data out of another feature has filled its own in.
 *
 * Each capture is a no-op unless the seeded body contains its block, so this is
 * one call for every template rather than a branch on the code -- which would
 * be a second place the template's identity is spelled out.
 */
async function seedContent(
  supabase: SupabaseClient,
  blocks: Block[],
  stageId: number | null,
  projectId: number | null
): Promise<Block[]> {
  const { blocks: withDrawings } = await captureDrawings(supabase, blocks, stageId, projectId);
  return captureAsConstructed(supabase, captureDesignCriteria(withDrawings), stageId, projectId);
}

/**
 * The signatory a certified document starts with.
 *
 * The first engineer the editor's own dropdown would offer -- same filter, same
 * order -- so the name in the signature block is the one that was already at
 * the top of the list, and the document never opens with an empty signature.
 * Somebody has to be nominated before it can be signed, and on a firm this size
 * there is normally one certifier; making the reader pick them every time was a
 * click that told nobody anything.
 *
 * Null when there is no certified engineer at all, which the editor shows as
 * "Select signatory..." exactly as it did before.
 */
async function firstCertifiedEngineer(supabase: SupabaseClient): Promise<string | null> {
  const { data, error } = await supabase
    .from('team_members')
    .select('id')
    .eq('type', 'Engineer')
    .eq('is_active', true)
    .eq('is_cpeng', true)
    .order('first_name')
    .limit(1);
  if (error) {
    // Not worth failing creation over: the document opens unsigned and the
    // dropdown still works.
    console.error('Could not pick a default certified signatory:', error);
    return null;
  }
  return data?.[0]?.id ?? null;
}

/**
 * Creates a document and returns its new id. Throws on failure so the caller can
 * surface the message.
 */
export async function createDocument(
  supabase: SupabaseClient,
  authUserId: string,
  params: CreateDocumentParams
): Promise<string> {
  const { templateId, projectId, stageId, title, templateCode, requiresCertifiedSignatory } = params;
  const chrome: DocumentChromeVariant = params.chrome ?? 'letter';

  // A site instruction's header values are OVERRIDES: blank means "derive live
  // from the project", and they are frozen into the document by
  // snapshot_document_header() when it is issued. Only the two with no project
  // behind them are written now -- the date, and the method, whose default is a
  // constant rather than something derived.
  const headerFields = chrome === 'site_instruction'
    ? {
        document_date: new Date().toISOString().split('T')[0],
        method: SI_METHOD_DEFAULT,
      }
    : await buildLetterHeaderFields(supabase, projectId, stageId ?? null, title);

  const { data: profileData } = await supabase
    .from('profiles')
    .select('team_member_id')
    .eq('id', authUserId)
    .single();

  const { data, error } = await supabase.rpc('create_document', {
    p_template_id: templateId,
    p_project_id: projectId,
    p_stage_id: stageId ?? null,
    p_title: title || null,
    p_header_fields: headerFields,
    // Seeded from the block types in config.ts rather than from
    // document_templates.blocks: the shape is defined in TypeScript, and a copy
    // in the database would go stale the next time a block type changes.
    p_content: await seedContent(supabase, createInitialBlocks(chrome, templateCode), stageId ?? null, projectId),
    p_signatory_id: requiresCertifiedSignatory
      ? await firstCertifiedEngineer(supabase)
      : (profileData?.team_member_id ?? null),
  });

  if (error || !data?.id) {
    throw new Error(error?.message || 'Error creating document');
  }

  return data.id as string;
}
